/**
 * Dropdown panel for generating or downloading the returns CSV report; used by RmaListExportReport.
 */
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DateTimeV2, { DATETIME_VIEW } from "../../../../../components/datetime-v2/datetime-v2";
import { useDateTimeFormattingParams } from "../../../../../hooks/use-datetime-formatting-params";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import "./rma-list-export-report-expanded.scss";
import { IReport } from "../../../../../services/api/report-service";
import ReportStore from "../../../../../stores/report-store";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";

export interface IRmaListExportReportExpandedProps {
    refElement: HTMLSpanElement | null;
    onClick: () => void;
    onGenerateNewClick: () => void;
    report?: IReport;
    isGeneratingReport: boolean;
    reportStore: ReportStore;
}

const RmaListExportReportExpanded: React.FC<IRmaListExportReportExpandedProps> = ({
    refElement,
    onClick,
    onGenerateNewClick,
    report,
    isGeneratingReport,
    reportStore,
}) => {
    const expandedBoxRef = useRef<HTMLDivElement>(null);
    const dateTimeParams = useDateTimeFormattingParams();
    const [position, setPosition] = useState({ x: 0, y: 0 });

    useLayoutEffect(() => {
        const getPosition = () => {
            const rect = refElement?.getBoundingClientRect();
            const expandedBoxRect = expandedBoxRef.current?.getBoundingClientRect();
            const expandedBoxWidth = expandedBoxRect ? expandedBoxRect.width : 0;
            if (rect) {
                const x = rect.x - (expandedBoxWidth - rect.width) + window.scrollX;
                const y = rect.y + rect.height + window.scrollY;
                setPosition({ x, y });
            }
        };

        if (refElement && expandedBoxRef.current) {
            getPosition();
        }
    }, [refElement, expandedBoxRef]);

    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";

    const onDownloadClick = () => {
        if (!report) {
            return;
        }
        window.open(report.file_path, "_blank");
    };

    return createPortal(
        <div className="rma-list-export-report-expanded">
            <div
                className="rma-list-export-report-expanded__overlay"
                onClick={(e) => {
                    e.stopPropagation();
                    onClick();
                }}
            />
            <div
                className="rma-list-export-report-expanded__panel"
                style={{ left: position.x, top: position.y }}
                onClick={(e) => e.stopPropagation()}
                ref={expandedBoxRef}
            >
                {!isGeneratingReport && (
                    <div onClick={onGenerateNewClick}>
                        <FontBody2>{getTranslation("GENERATE_NEW_REPORT", scope)}</FontBody2>
                    </div>
                )}
                <div onClick={onDownloadClick}>
                    <FontBody2>{getTranslation("DOWNLOAD_LAST_REPORT", scope)}</FontBody2>
                    <FontBody2>
                        <DateTimeV2
                            {...dateTimeParams}
                            utcDateString={reportStore.getLastGeneratedUTCString()}
                            dateTimeView={DATETIME_VIEW.FULL}
                        />
                    </FontBody2>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default RmaListExportReportExpanded;
